import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  VideoCamera, UploadSimple, Plus, CaretLeft, PaperPlaneTilt, CheckCircle,
  Clock, Trash, ArrowsClockwise, WarningOctagon, Timer,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import VerifiedBadge from "@/components/VerifiedBadge";

const MAX_MB = 50;
const MAX_SEC = 60;

const fmtTs = (s) => {
  if (s == null) return "";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const statusColor = (s) => ({
  requested: "text-yellow-400 border-yellow-400/30",
  submitted: "text-brand-accent border-brand-accent/40",
  reviewed: "text-green-400 border-green-400/30",
}[s] || "text-text-secondary border-white/10");

export default function Reviews() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeId, setActiveId] = useState(params.get("id"));

  const load = async () => {
    try {
      const { data } = await api.get("/form-reviews");
      setReviews(data || []);
    } catch {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/my/coaches").then(({ data }) => setCoaches(data || [])).catch(() => {});
    api.get("/coach/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const id = params.get("id");
    setActiveId(id);
  }, [params]);

  const isCoach = user?.is_coach;
  const hasCoach = coaches.length > 0;

  if (activeId) {
    return (
      <ReviewDetail
        reviewId={activeId}
        currentUserId={user?.user_id}
        onClose={() => { setParams({}); load(); }}
      />
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="reviews-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tiny text-brand-accent mb-2">// FORM REVIEW</div>
          <h1 className="font-heading text-4xl sm:text-5xl">VIDEO REVIEWS</h1>
          <p className="text-text-secondary text-sm mt-2">
            {isCoach && hasCoach
              ? "Send videos to your coach and review your clients."
              : isCoach
              ? "Request videos and give timestamped feedback to your clients."
              : "Send workout videos to your coach for timestamped feedback."}
          </p>
        </div>
        <div className="flex gap-2">
          {hasCoach && (
            <button
              onClick={() => setCreating(true)}
              className="btn-kinetic px-4 py-2.5 text-xs label-tiny tracking-widest flex items-center gap-2"
              data-testid="new-review-button"
            >
              <UploadSimple size={14} weight="bold" /> SEND VIDEO
            </button>
          )}
          {isCoach && (
            <button
              onClick={() => setRequesting(true)}
              className="border border-white/10 hover:border-brand-accent hover:text-brand-accent transition-colors px-4 py-2.5 text-xs label-tiny tracking-widest flex items-center gap-2"
              data-testid="request-review-button"
            >
              <Plus size={14} weight="bold" /> REQUEST FROM CLIENT
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="label-tiny text-text-secondary">LOADING...</div>
      ) : reviews.length === 0 ? (
        <div className="tactical-card p-10 text-center" data-testid="reviews-empty">
          <VideoCamera size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
          <p className="text-sm text-text-secondary">
            No reviews yet. {hasCoach || isCoach ? "Start one above." : "Once you have a coach, video reviews will appear here."}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reviews-list">
          {reviews.map((r) => (
            <button
              key={r.review_id}
              onClick={() => setParams({ id: r.review_id })}
              className="text-left tactical-card p-5 hover:bg-surface-elevated transition-colors"
              data-testid={`review-card-${r.review_id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`label-tiny border px-2 py-0.5 ${statusColor(r.status)}`}>
                  {r.status.toUpperCase()}
                </span>
                <span className="label-tiny text-text-secondary">
                  {r.role === "coach" ? "CLIENT" : "COACH"} · {r.other?.name?.split(" ")[0] || "—"}
                </span>
              </div>
              <div className="font-heading text-lg mb-1">{r.exercise?.toUpperCase()}</div>
              <p className="text-xs text-text-secondary mb-3 line-clamp-2">
                {r.client_notes || r.coach_request_notes || <em>No notes</em>}
              </p>
              <div className="flex items-center justify-between label-tiny text-text-secondary">
                <span className="flex items-center gap-1">
                  <Clock size={11} /> {new Date(r.created_at).toLocaleDateString()}
                </span>
                <span>{(r.comments || []).length} COMMENTS</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && (
        <CreateReviewDialog
          coaches={coaches}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
      {requesting && (
        <RequestReviewDialog
          clients={clients}
          onClose={() => setRequesting(false)}
          onCreated={() => { setRequesting(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateReviewDialog({ coaches, onClose, onCreated }) {
  const [form, setForm] = useState({
    coach_id: coaches[0]?.coach?.user_id || "",
    exercise: "",
    client_notes: "",
  });
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!form.coach_id && coaches[0]?.coach?.user_id) {
      setForm((f) => ({ ...f, coach_id: coaches[0].coach.user_id }));
    }
  }, [coaches, form.coach_id]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Video too large (max ${MAX_MB}MB)`);
      return;
    }
    if (!f.type.startsWith("video/")) {
      toast.error("Please pick a video file");
      return;
    }
    setFile(f);
    // Read duration (best-effort — some codecs may not decode in browser)
    const url = URL.createObjectURL(f);
    const v = document.createElement("video");
    v.preload = "metadata";
    let settled = false;
    const cleanup = () => { URL.revokeObjectURL(url); };
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      setDuration(null);  // allow upload without duration
      toast.info("Couldn't read video length — will upload anyway");
    }, 4000);
    v.onloadedmetadata = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      cleanup();
      if (v.duration > MAX_SEC + 0.5) {
        toast.error(`Video too long (max ${MAX_SEC}s)`);
        setFile(null);
        setDuration(null);
        return;
      }
      setDuration(v.duration);
    };
    v.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      cleanup();
      setDuration(null);
      toast.info("Couldn't read video metadata — will upload anyway");
    };
    v.src = url;
  };

  const submit = async () => {
    if (!form.coach_id) return toast.error("Pick a coach");
    if (!form.exercise.trim()) return toast.error("Add an exercise name");
    if (!file) return toast.error("Attach a video");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/form-reviews/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/form-reviews", {
        coach_id: form.coach_id,
        exercise: form.exercise.trim(),
        client_notes: form.client_notes.trim() || null,
        video_url: up.url,
        video_duration_sec: duration,
      });
      toast.success("Video sent for review");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-lg" data-testid="create-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">SEND VIDEO TO COACH</DialogTitle>
          <DialogDescription className="sr-only">Upload a short workout video and send it to your coach for review.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {coaches.length === 0 ? (
            <p className="text-sm text-text-secondary">You need an active coach first.</p>
          ) : (
            <>
              <div>
                <label className="label-tiny block mb-2">COACH</label>
                <Select value={form.coach_id} onValueChange={(v) => setForm({ ...form, coach_id: v })}>
                  <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="review-coach-select">
                    <SelectValue placeholder="Pick a coach" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                    {coaches.map((c) => (
                      <SelectItem key={c.coach?.user_id || c.link_id} value={c.coach?.user_id}>
                        <span className="inline-flex items-center gap-1.5">
                          {c.coach?.name}
                          <VerifiedBadge verified={c.coach?.is_verified} size="xs" />
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-tiny block mb-2">EXERCISE</label>
                <Input
                  value={form.exercise}
                  onChange={(e) => setForm({ ...form, exercise: e.target.value })}
                  placeholder="Back Squat"
                  className="bg-surface-elevated border-white/10"
                  data-testid="review-exercise-input"
                />
              </div>
              <div>
                <label className="label-tiny block mb-2">NOTE (optional)</label>
                <Textarea
                  value={form.client_notes}
                  onChange={(e) => setForm({ ...form, client_notes: e.target.value })}
                  placeholder="Working weight was 315 for 3. Felt shaky at the bottom."
                  className="bg-surface-elevated border-white/10"
                  data-testid="review-notes-input"
                />
              </div>
              <div>
                <label className="label-tiny block mb-2">VIDEO (MP4/MOV · MAX {MAX_MB}MB · {MAX_SEC}s)</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="video/*"
                  onChange={onPick}
                  className="hidden"
                  data-testid="review-video-input"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full border border-dashed border-white/20 hover:border-brand-accent transition-colors p-6 text-center"
                  data-testid="review-pick-video"
                >
                  {file ? (
                    <div>
                      <VideoCamera size={24} weight="duotone" className="text-brand-accent mx-auto mb-1" />
                      <div className="text-sm">{file.name}</div>
                      <div className="label-tiny text-text-secondary mt-1">
                        {(file.size / 1024 / 1024).toFixed(1)}MB {duration && `· ${fmtTs(duration)}`}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <UploadSimple size={24} weight="duotone" className="text-brand-accent mx-auto mb-1" />
                      <div className="label-tiny">TAP TO PICK VIDEO</div>
                    </div>
                  )}
                </button>
              </div>
              <button
                onClick={submit}
                disabled={uploading || !file}
                className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50"
                data-testid="review-submit-button"
              >
                {uploading ? "UPLOADING..." : "SEND FOR REVIEW"}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RequestReviewDialog({ clients, onClose, onCreated }) {
  const [form, setForm] = useState({
    client_id: clients[0]?.client?.user_id || "",
    exercise: "",
    coach_request_notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form.client_id && clients[0]?.client?.user_id) {
      setForm((f) => ({ ...f, client_id: clients[0].client.user_id }));
    }
  }, [clients, form.client_id]);

  const submit = async () => {
    if (!form.client_id) return toast.error("Pick a client");
    if (!form.exercise.trim()) return toast.error("Add an exercise");
    setSaving(true);
    try {
      await api.post("/coach/form-reviews/request", {
        client_id: form.client_id,
        exercise: form.exercise.trim(),
        coach_request_notes: form.coach_request_notes.trim() || null,
      });
      toast.success("Request sent to client");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-lg" data-testid="request-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">REQUEST VIDEO FROM CLIENT</DialogTitle>
          <DialogDescription className="sr-only">Ask a client to send you a form-check video of a specific exercise.</DialogDescription>
        </DialogHeader>
        {clients.length === 0 ? (
          <p className="text-sm text-text-secondary">Add a client to your roster first.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="label-tiny block mb-2">CLIENT</label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="req-client-select">
                  <SelectValue placeholder="Pick a client" />
                </SelectTrigger>
                <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                  {clients.map((c) => (
                    <SelectItem key={c.client?.user_id} value={c.client?.user_id}>
                      {c.client?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-tiny block mb-2">EXERCISE</label>
              <Input
                value={form.exercise}
                onChange={(e) => setForm({ ...form, exercise: e.target.value })}
                placeholder="Back Squat"
                className="bg-surface-elevated border-white/10"
                data-testid="req-exercise-input"
              />
            </div>
            <div>
              <label className="label-tiny block mb-2">WHAT DO YOU WANT TO SEE?</label>
              <Textarea
                value={form.coach_request_notes}
                onChange={(e) => setForm({ ...form, coach_request_notes: e.target.value })}
                placeholder="Side-angle, single working set at your top weight."
                className="bg-surface-elevated border-white/10"
                data-testid="req-notes-input"
              />
            </div>
            <button
              onClick={submit}
              disabled={saving}
              className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50"
              data-testid="req-submit-button"
            >
              {saving ? "SENDING..." : "SEND REQUEST"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReviewDetail({ reviewId, currentUserId, onClose }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [captureTs, setCaptureTs] = useState(true);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [duration, setDuration] = useState(null);
  const videoRef = useRef();
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/form-reviews/${reviewId}`);
      setReview(data);
    } catch {
      toast.error("Not found or no access");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [reviewId]);
  useEffect(() => {
    const iv = setInterval(load, 12000);
    return () => clearInterval(iv);
  }, [reviewId]);

  const currentVideoTime = () => {
    const v = videoRef.current;
    if (!v || isNaN(v.currentTime)) return null;
    return Math.max(0, v.currentTime);
  };

  const jumpTo = (sec) => {
    const v = videoRef.current;
    if (!v || sec == null) return;
    v.currentTime = sec;
    v.play?.().catch(() => {});
  };

  const postComment = async () => {
    if (posting) return;
    const text = commentText.trim();
    if (!text) return toast.error("Type a comment");
    setPosting(true);
    try {
      const ts = captureTs ? currentVideoTime() : null;
      await api.post(`/form-reviews/${reviewId}/comments`, { text, timestamp_sec: ts });
      setCommentText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setPosting(false);
    }
  };

  const completeReview = async () => {
    try {
      await api.post(`/form-reviews/${reviewId}/complete`);
      toast.success("Marked as reviewed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteReview = async () => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/form-reviews/${reviewId}`);
      toast.success("Deleted");
      onClose();
    } catch {
      toast.error("Failed");
    }
  };

  const onSubmitVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Max ${MAX_MB}MB`); return; }
    if (!f.type.startsWith("video/")) { toast.error("Pick a video"); return; }

    // Probe duration (best-effort with error + timeout fallback)
    const probeDuration = () => new Promise((resolve) => {
      const url = URL.createObjectURL(f);
      const probe = document.createElement("video");
      probe.preload = "metadata";
      let settled = false;
      const finish = (val) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(url);
        resolve(val);
      };
      const timeout = setTimeout(() => finish(null), 4000);
      probe.onloadedmetadata = () => { clearTimeout(timeout); finish(probe.duration); };
      probe.onerror = () => { clearTimeout(timeout); finish(null); };
      probe.src = url;
    });

    const dur = await probeDuration();
    if (dur != null && dur > MAX_SEC + 0.5) {
      toast.error(`Max ${MAX_SEC}s`);
      return;
    }
    if (dur == null) toast.info("Couldn't read video length — uploading anyway");

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/form-reviews/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post(`/form-reviews/${reviewId}/upload`, null, {
        params: dur != null ? { video_url: up.url, duration_sec: dur } : { video_url: up.url },
      });
      setDuration(dur);
      toast.success("Video submitted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading || !review) {
    return <div className="p-10 label-tiny text-text-secondary" data-testid="review-detail-loading">LOADING...</div>;
  }

  const isCoach = review.role === "coach";
  const canSubmitVideo = review.status === "requested" && !isCoach;

  const sortedComments = [...(review.comments || [])].sort(
    (a, b) => (a.timestamp_sec ?? Infinity) - (b.timestamp_sec ?? Infinity) ||
              (a.created_at || "").localeCompare(b.created_at || "")
  );

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="review-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <button
          onClick={onClose}
          className="flex items-center gap-1 label-tiny text-text-secondary hover:text-white transition-colors"
          data-testid="review-back-button"
        >
          <CaretLeft size={14} /> BACK TO REVIEWS
        </button>
        <div className="flex items-center gap-2">
          <span className={`label-tiny border px-2 py-0.5 ${statusColor(review.status)}`}>
            {review.status.toUpperCase()}
          </span>
          <button
            onClick={deleteReview}
            className="text-text-secondary hover:text-red-400 transition-colors"
            data-testid="review-delete-button"
            aria-label="Delete review"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>

      {/* Metadata */}
      <div className="mb-6">
        <div className="label-tiny text-brand-accent mb-1">// FORM REVIEW</div>
        <h1 className="font-heading text-3xl mb-2" data-testid="review-exercise">{review.exercise?.toUpperCase()}</h1>
        <div className="flex items-center gap-4 label-tiny text-text-secondary">
          <span className="inline-flex items-center gap-1">
            COACH: {review.coach?.name}
            <VerifiedBadge verified={review.coach?.is_verified} size="xs" />
          </span>
          <span>·</span>
          <span>CLIENT: {review.client?.name}</span>
          <span>·</span>
          <span>{new Date(review.created_at).toLocaleString()}</span>
        </div>
        {(review.coach_request_notes || review.client_notes) && (
          <div className="mt-3 p-3 border border-white/10 bg-surface-elevated text-sm">
            {review.coach_request_notes && (
              <div className="mb-2">
                <span className="label-tiny text-brand-accent">COACH ASKED FOR:</span>
                <div className="text-text-secondary mt-1">{review.coach_request_notes}</div>
              </div>
            )}
            {review.client_notes && (
              <div>
                <span className="label-tiny text-brand-accent">CLIENT NOTES:</span>
                <div className="text-text-secondary mt-1">{review.client_notes}</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Video column */}
        <div className="lg:col-span-3">
          {review.video_url ? (
            <video
              ref={videoRef}
              src={review.video_url}
              controls
              className="w-full bg-black border border-white/10"
              data-testid="review-video-player"
            />
          ) : (
            <div className="border border-dashed border-white/20 p-10 text-center" data-testid="review-no-video">
              <VideoCamera size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
              {canSubmitVideo ? (
                <div>
                  <p className="text-sm mb-4">
                    Your coach requested a video of <span className="text-brand-accent">{review.exercise}</span>.
                  </p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="video/*"
                    onChange={onSubmitVideo}
                    className="hidden"
                    data-testid="review-submit-video-input"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="btn-kinetic px-5 py-2.5 text-xs label-tiny tracking-widest disabled:opacity-50"
                    data-testid="review-submit-video-button"
                  >
                    {uploading ? "UPLOADING..." : "UPLOAD YOUR VIDEO"}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-text-secondary">
                  <WarningOctagon size={16} weight="duotone" className="inline mr-1 text-yellow-400" />
                  Waiting for {review.client?.name} to upload the video.
                </p>
              )}
            </div>
          )}

          {review.video_url && isCoach && review.status === "submitted" && (
            <button
              onClick={completeReview}
              className="btn-kinetic w-full mt-3 py-2.5 text-xs label-tiny tracking-widest flex items-center justify-center gap-2"
              data-testid="review-complete-button"
            >
              <CheckCircle size={13} weight="fill" /> MARK AS REVIEWED
            </button>
          )}
        </div>

        {/* Comments column */}
        <div className="lg:col-span-2">
          <div className="border border-white/10 bg-surface-DEFAULT">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="label-tiny">TIMESTAMPED COMMENTS ({sortedComments.length})</span>
              <button
                onClick={load}
                className="text-text-secondary hover:text-white transition-colors"
                data-testid="review-comments-refresh"
                aria-label="Refresh"
              >
                <ArrowsClockwise size={13} />
              </button>
            </div>
            <div className="max-h-[380px] overflow-y-auto p-3 space-y-2" data-testid="review-comments-list">
              {sortedComments.length === 0 ? (
                <div className="text-xs text-text-secondary text-center py-8">
                  No comments yet. Click into the video and drop a timestamped cue.
                </div>
              ) : (
                sortedComments.map((c) => {
                  const mine = c.author_id === currentUserId;
                  return (
                    <div
                      key={c.comment_id}
                      className={`border p-2 text-sm ${mine ? "border-brand-accent/40 bg-brand-accent/5" : "border-white/10 bg-surface-elevated"}`}
                      data-testid={`review-comment-${c.comment_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        {c.timestamp_sec != null ? (
                          <button
                            onClick={() => jumpTo(c.timestamp_sec)}
                            className="label-tiny text-brand-accent hover:underline flex items-center gap-1"
                            data-testid={`review-comment-jump-${c.comment_id}`}
                          >
                            <Timer size={10} weight="duotone" /> {fmtTs(c.timestamp_sec)}
                          </button>
                        ) : (
                          <span className="label-tiny text-text-secondary/60">GENERAL</span>
                        )}
                        <span className="label-tiny text-text-secondary/60 text-[9px]">
                          {new Date(c.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        </span>
                      </div>
                      <div className="whitespace-pre-wrap">{c.text}</div>
                    </div>
                  );
                })
              )}
            </div>

            {review.video_url && (
              <div className="border-t border-white/10 p-3 space-y-2">
                <Textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={captureTs ? "Comment at current moment..." : "General comment..."}
                  className="bg-surface-elevated border-white/10 text-sm min-h-[60px]"
                  data-testid="review-comment-input"
                />
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-1.5 label-tiny cursor-pointer">
                    <input
                      type="checkbox"
                      checked={captureTs}
                      onChange={(e) => setCaptureTs(e.target.checked)}
                      data-testid="review-comment-ts-toggle"
                      className="accent-brand-accent"
                    />
                    ATTACH TIMESTAMP {captureTs && videoRef.current?.currentTime != null && `(${fmtTs(videoRef.current.currentTime)})`}
                  </label>
                  <button
                    onClick={postComment}
                    disabled={posting || !commentText.trim()}
                    className="btn-kinetic px-4 py-2 text-xs label-tiny disabled:opacity-50 flex items-center gap-1"
                    data-testid="review-comment-send"
                  >
                    <PaperPlaneTilt size={11} weight="bold" /> {posting ? "..." : "POST"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
